<template>
  <div class="text-reply">
    <div class="panel">
      <div class="panel-header">
        <h2 class="panel-title"><i class="fas fa-comment-dots"></i> 文字回复功能</h2>
        <div class="header-actions">
          <button class="btn btn-success" @click="addKeyword">+ 添加回复内容</button>
          <button class="btn btn-import" @click="importKeywords">导入</button>
          <button class="btn btn-export" @click="exportKeywords">导出</button>
          <button class="btn btn-danger" @click="clearKeywords">清空</button>


          <button
              class="btn btn-field-control"
              :class="{ 'btn-field-control-active': status }"
              @click="toggleStatus"
          >
            {{ status ? '停止文字回复' : '开始文字回复' }}
          </button>

          <button class="btn btn-ai" :class="{ 'ai-enabled': aiStatus }" @click="toggleAI"><i class="fas fa-robot"></i>
            {{ aiStatus ? 'AI已接管' : '开始AI回复' }}
          </button>
        </div>
      </div>

      <div class="control-settings">
        <div class="setting-row">
          <div class="setting-item">
            <label>回复组</label>
            <div class="custom-select" @click="toggleGroupDropdown">
              <div class="custom-select-value">
                {{ getSelectedGroupName }}
              </div>
              <i class="fas fa-chevron-down select-arrow"></i>
              <div v-if="showGroupDropdown" class="custom-select-dropdown">
                <div
                    class="dropdown-item"
                    :class="{ selected: config.group_id == 0 }"
                    @click="selectGroup(0)"
                >
                  默认回复组
                </div>
                <div
                    v-for="group in groups"
                    :key="group.id"
                    class="dropdown-item group-item"
                    :class="{ selected: config.group_id == group.id }"
                    @click.stop="selectGroup(group.id)"
                >
                  <span>{{ group.name }}</span>
                  <button
                      class="delete-group-btn"
                      @click.stop="deleteGroup(group.id)"
                      title="删除此回复组"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
            <button class="btn btn-sm btn-secondary" @click="showImageModal = true">+ 新增</button>
          </div>


          <div class="setting-item">
            <label>回复顺序</label>
            <div class="custom-select" @click="toggleOrderDropdown">
              <div class="custom-select-value">
                {{ config.control_order == 0 ? '正常顺序' : '随机顺序' }}
              </div>
              <i class="fas fa-chevron-down select-arrow"></i>
              <div v-if="showOrderDropdown" class="custom-select-dropdown">
                <div
                    class="dropdown-item"
                    :class="{ selected: config.control_order == 0 }"
                    @click.stop="selectOrder(0)"
                >
                  正常顺序
                </div>
                <div
                    class="dropdown-item"
                    :class="{ selected: config.control_order == 1 }"
                    @click.stop="selectOrder(1)"
                >
                  随机顺序
                </div>
              </div>
            </div>
          </div>

          <div class="setting-item">
            <label>回复频率</label>
            <div class="frequency-control">
              <input type="number" v-model.number="config.send_freq_min" class="frequency-input" min="1" max="300"
                     @change="updateConfig">
              <span class="frequency-separator">~</span>
              <input type="number" v-model.number="config.send_freq_max" class="frequency-input" min="1" max="300"
                     @change="updateConfig">
              <span class="frequency-unit">秒</span>
            </div>
          </div>

          <div class="checkbox-group">

            <label class="checkbox-item">
              <input type="checkbox" v-model="config.rand_space" @change="updateConfig">
              <span class="checkbox-custom"></span>
              <span>随机空格</span>
            </label>

          </div>
        </div>
      </div>

      <div class="keywords-list">
        <div class="list-header">
          <div class="header-command">序号</div>

          <div class="header-keyword">关键词</div>
          <div class="header-content">回复内容</div>
          <div class="header-actions">操作</div>
        </div>

        <div v-if="keywords?.list?.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <p>暂无关键词</p>
        </div>

        <div v-for="(keyword, index) in keywords?.list" :key="keyword.id" class="keyword-item">
          <div class="item-command">{{ (queryForm.page - 1) * queryForm.limit + index + 1 }}</div>

          <div class="item-keyword" :title="keyword.keywords">{{ keyword.keywords }}</div>
          <div class="item-content" :title="keyword.content">{{ keyword.content }}</div>
          <div class="item-actions">
            <button class="btn-action edit" @click="editKeyword(keyword)"><i class="fas fa-edit"></i></button>
            <button class="btn-action delete" @click="deleteKeyword(keyword.id)"><i class="fas fa-trash"></i></button>
          </div>
        </div>
      </div>

      <div class="pagination-container">
        <div class="pagination">
          <button class="page-btn" @click="previousPage" :disabled="queryForm.page === 1">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="page-info">
            第 {{ queryForm.page }} 页，共 {{ queryForm.pageCount }} 页
          </div>
          <button class="page-btn" @click="nextPage" :disabled="queryForm.page === queryForm.pageCount">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 图片功能弹窗 -->
    <div class="modal" v-if="showImageModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>新增回复组</h3>
          <button class="close-modal" @click="showImageModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>回复组名称</label>
            <input type="text" v-model="newGroupName" placeholder="请输入回复组名称" class="form-input">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showImageModal = false">取消</button>
          <button class="btn btn-primary" @click="saveGroup">保存</button>
        </div>
      </div>
    </div>

    <!-- 添加/编辑关键词弹窗 -->
    <div class="modal" v-if="showKeywordModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingKeyword ? '编辑回复内容' : '添加回复内容' }}</h3>
          <button class="close-modal" @click="showKeywordModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>关键词</label>
            <input type="text" v-model="currentKeyword.keywords" placeholder="请输入关键词，多个用空格隔开"
                   class="form-input">
          </div>
          <div class="form-group">
            <label>回复内容</label>
            <textarea v-model="currentKeyword.content" placeholder="请输入回复内容,单行建议不要超过45字。" class="form-textarea"
                      rows="10"></textarea>
            <div class="insert-content">
              <div class="weight-range">
                <input type="number" v-model="weightFrom" class="range-input">
                <span class="range-label">到</span>
                <input type="number" v-model="weightTo" class="range-input">
                <span class="range-equal">=</span>
                <input type="text" v-model="weightContent" class="range-text" placeholder="应对回复内容">
                <button class="btn btn-variable-small" @click="insertWeightRange">插入体重区间</button>
              </div>
              <div class="variable-tools">
                <button type="button" class="btn btn-variable-main" @click="applyVariables">
                  <i class="fas fa-robot"></i> 一键变量洗稿
                </button>

              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showKeywordModal = false">取消</button>
          <button class="btn btn-primary" @click="saveKeyword">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/utils/http";
import {chat} from "@/utils/Ai";
import {delay, extractChineseCharacters, getRandomInt, getYmdHm} from "@/utils/function";

export default {
  name: 'ViolationVerification',
  data() {
    return {

      msgStatus: 1,
      config: {},
      queryForm: {
        group_id: 0,
        limit: 4,
        page: 1,
        pageCount: 0,
      },
      groups: [],
      showGroupDropdown: false,
      showOrderDropdown: false,
      weightFrom: '',
      weightTo: '',
      weightContent: '',
      showKeywordModal: false,
      showImageModal: false,
      newGroupName: '',
      editingKeyword: null,
      currentKeyword: {
        keywords: '',
        content: '',
      },
      keywords: []
    }
  },
  computed: {
    apiKey(){

      return this.$store.state.apiKey;
    },

    aiStatus() {
      return this.$store.state.replyAiStatus
    },
    status() {
      return this.$store.state.replyStatus
    },
    replyIndex() {
      return this.$store.state.replyIndex
    },
    userInfo() {
      return this.$store.state.userInfo;
    },

    getSelectedGroupName() {
      if (this.config.group_id == 0) {
        return '默认回复组'
      }
      const group = this.groups.find(g => g.id === this.config.group_id)
      return group ? group.name : '默认回复组'
    },


  },
  async mounted() {
    await this.getGroup();
    await this.getConfig();
    await this.getControlList();
    this.user = this.$store.state.userInfo;

    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    async getConfig() {
      const res = await http.post("api/field_control/getConfig", {type: "reply"});
      this.config = res.data;
    },
    async getGroup() {
      const res = await http.get("api/field_control/getGroup");
      this.groups = res.data
    },

    async applyVariables() {

      if (!this.apiKey) {
        this.$dialog.tips({
          type: "error",
          message: "未对接API,请在直播管理右侧->更多服务->API对接中配置API"
        })
        return;

      }
      let content = this.currentKeyword.content;

      this.$dialog.loading({text: "增加变量中，请稍等..."})
      const result = await chat(this.$store.state.appids.var_id, this.apiKey, content).catch((message) => {
        this.$dialog.closeLoading();
        this.$dialog.alert({
          title: "温馨提示",
          message
        })
      });
      this.$dialog.closeLoading();

      if (result) {
        this.currentKeyword.content = result
        this.$dialog.tips({
          message: `已为您提供变量`
        })
      }


    },

    async getControlList() {
      this.queryForm.group_id = this.config.group_id;
      const res = await http.post("api/field_control/replyControlList", this.queryForm);
      this.keywords = res.data;
      this.queryForm.pageCount = res.data.pageCount

    },


    async updateConfigControl(refresh = false, tips = true) {
      const res = await this.updateConfig(tips);
      if (refresh) {
        this.getControlList();
      }

    },
    async updateConfig(tips = true) {
      const res = await http.post("api/field_control/updateConfig", this.config);
      if (tips) {
        if (res.code == 200) {
          this.$dialog.tips({
            message: res.msg,
            type: "success"
          })
        } else {
          this.$dialog.tips({
            message: res.msg,
            type: "error"
          })
        }
      }

      return res;
    },


    addKeyword() {
      this.editingKeyword = null
      this.currentKeyword = {
        keywords: '',
        content: '',
      }
      this.showKeywordModal = true
    },
    editKeyword(keyword) {
      this.editingKeyword = keyword
      this.currentKeyword = {...keyword}
      this.showKeywordModal = true
    },

    insertWeightRange() {
      if (!this.weightFrom || !this.weightTo || !this.weightContent) {
        this.$dialog.tips({
          message: "请填写完整的体重区间信息",
          type: "error"
        })
        return
      }
      const from = parseInt(this.weightFrom)
      const to = parseInt(this.weightTo)
      if (isNaN(from) || isNaN(to) || from > to) {
        this.$dialog.tips({
          message: "请输入有效的体重范围",
          type: "error"
        })
        return
      }
      const keywords = []
      for (let i = from; i <= to; i++) {
        keywords.push(i.toString())
      }
      this.currentKeyword.keywords = keywords.join(' ')
      this.currentKeyword.content = this.weightContent
      this.weightFrom = ''
      this.weightTo = ''
      this.weightContent = ''
    },
    async saveKeyword() {
      if (!this.currentKeyword.keywords || !this.currentKeyword.content) return

      if (this.editingKeyword) {

        const res = await http.post("api/field_control/updateReplyControl", {
          id: this.currentKeyword.id,
          content: this.currentKeyword.content,
          keywords: this.currentKeyword.keywords,

        });

        if (res.code == 200) {
          this.showKeywordModal = false

          this.$dialog.tips({message: res.msg, type: "success"});
          this.getControlList();
          return;
        }
        this.$dialog.tips({message: res.msg, type: "error"});

        // 编辑
      } else {
        // 添加

        const res = await http.post("api/field_control/addReplyControl", {
          group_id: this.config.group_id,
          content: this.currentKeyword.content,
          keywords: this.currentKeyword.keywords,
        });

        if (res.code == 200) {
          this.showKeywordModal = false
          this.$dialog.tips({message: res.msg, type: "success"});
          this.getControlList();
          return;
        }
        this.$dialog.tips({message: res.msg, type: "error"});


      }
    },
    async deleteKeyword(id) {

      const confirm = await this.$dialog.confirm({
        title: '确认删除',
        message: '确定要删除？删除后将无法恢复。',
        okText: '删除',
        cancelText: '取消'
      }).catch(() => false)

      if (confirm !== false) {
        const res = await http.post("api/field_control/delControl", {type: "reply", id})
        if (res.code == 200) {
          this.getControlList();
          this.$dialog.tips({
            message: res.msg,
            type: "success"
          })

          return;
        }
        this.$dialog.tips({
          message: res.msg,
          type: "error"
        })
      }


    },

    async saveGroup() {

      if (!this.newGroupName) {
        this.$dialog.tips({
          message: "请输入回复组名称",
          type: "error"
        });
        return
      }
      const res = await http.post("api/field_control/addGroup", {name: this.newGroupName})
      if (res.code == 200) {
        this.getGroup();
        this.config.group_id = parseInt(res.data);
        this.updateConfigControl(true, false)
        this.$dialog.tips({message: res.msg, type: "success"})
        this.newGroupName = ''
        this.showImageModal = false
        return;
      }


    },


    toggleGroupDropdown() {
      this.showGroupDropdown = !this.showGroupDropdown
      if (this.showGroupDropdown) this.showOrderDropdown = false
    },

    toggleOrderDropdown() {
      this.showOrderDropdown = !this.showOrderDropdown
      if (this.showOrderDropdown) this.showGroupDropdown = false
    },

    handleClickOutside(event) {
      const customSelect = event.target.closest('.custom-select')
      if (!customSelect) {
        this.showGroupDropdown = false
        this.showOrderDropdown = false
      }
    },

    selectGroup(groupId) {
      this.config.group_id = groupId
      this.showGroupDropdown = false
      this.updateConfigControl(true)
    },

    selectOrder(order) {
      this.control_order = order
      this.showOrderDropdown = false
      this.updateConfig()
    },

    async deleteGroup(groupId) {
      const confirm = await this.$dialog.confirm({
        title: '确认删除',
        message: '确定要删除这个场控组吗？删除后将无法恢复。',
        okText: '删除',
        cancelText: '取消'
      }).catch(() => false)

      if (confirm !== false) {
        const res = await http.post("api/field_control/deleteGroup", {id: groupId})
        if (res.code == 200) {
          this.$dialog.tips({message: res.msg, type: "success"})
          await this.getGroup()
          if (this.config.group_id === groupId) {
            this.config.group_id = 0
            this.updateConfigControl(true, false)

          }
        } else {
          this.$dialog.tips({message: res.msg, type: "error"})
        }
      }
    },
    importKeywords() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.txt'
      input.onchange = (e) => {
        const file = e.target.files[0]
        if (file) {
          const reader = new FileReader()
          reader.onload = async (event) => {
            try {
              const text = event.target.result;
              console.log(text)
              const res = await http.post("api/field_control/batchImport", {
                type: "reply",
                group_id: this.config.group_id,
                contents: text
              })

              if (res.code == 200) {
                this.getControlList();
                this.$dialog.tips({
                  message: res.msg,
                  type: "success"
                })
                return;
              }
              this.$dialog.tips({
                message: res.msg,
                type: "error"
              })

            } catch (error) {
              this.$dialog.tips({
                message: "导入失败：文件解析错误",
                type: "success"
              })
            }
          }
          reader.readAsText(file)
        }
      }
      input.click()
    },
    async exportKeywords() {
      const res = await http.post("api/field_control/export", {type: "reply", group_id: this.config.group_id});

      if (res.code == 200) {
        const dataBlob = new Blob([res.data.text], {type: 'text/plain'})
        const url = URL.createObjectURL(dataBlob)
        const link = document.createElement('a')
        link.href = url
        link.download = res.data.title;
        link.click()
        URL.revokeObjectURL(url)
      }

    },
    async clearKeywords() {

      const confirm = await this.$dialog.confirm({
        title: '温馨提示',
        message: '确定要清空所有场控内容吗',
      }).catch(() => false)

      console.log(confirm)
      if (confirm !== false) {
        const res = await http.post("api/field_control/clearControl", {type: 'reply', group_id: this.config.group_id})
        if (res.code == 200) {
          this.getControlList();
          this.$dialog.tips({
            message: res.msg,
            type: "success"
          })
          return;
        }
        this.$dialog.tips({
          message: res.msg,
          type: "error"
        })
      }
    },
    async toggleStatus() {
      if (!this.$store.state.liveWs) {
        this.$dialog.tips({
          message: "请先到直播账号管理，连接直播间",
          type: "error"
        });
        this.$store.commit("setReplyStatus", false)
        return;
      }

      this.$store.commit("setReplyStatus", !this.status)
      if (this.status) {
        this.startLiveKeyword();
        this.$dialog.tips({
          message: "文字回复已开启",
          type: "success"
        })
      } else {
        this.$store.commit("setReplyAiStatus", false)
        this.$dialog.tips({
          message: "文字回复已关闭",
          type: "success"
        })
      }

    },
    createTimer() {
      const num = getRandomInt(this.send_freq_min, this.send_freq_max) * 1000;

      if(num){
        console.log("等待"+num);

        setTimeout(() => {
          this.msgStatus = 1;
          console.log("等待结束");


        }, num)
      }else{
        this.msgStatus = 1;
        console.log("等待结束");

      }


    },
    async startLiveKeyword() {

      if(this.$store.state.replyStatus){
        if (this.$store.state.liveData.messageData.nickname != this.$store.state.userInfo?.platform.nickname && this.msgStatus) {
          if (this.$store.state.textKeyword != this.$store.state.liveData.messageData.content) {
            this.msgStatus = 0;
            this.createTimer();

            this.$store.commit("setTextKeyword", this.$store.state.liveData.messageData.content);
            const res = await http.post("api/auto/autoReply", {
              nickname: this.$store.state.liveData.messageData.nickname,
              content: this.$store.state.liveData.messageData.content,
              index: this.replyIndex,
              is_ai: this.aiStatus,
              status: this.status
            });
            if (res.code == 200) {
              this.$store.commit("setReplyIndex", this.replyIndex + 1);
            }

          }
        }
        await delay(1000)
        this.startLiveKeyword();
      }

    },

    async toggleAI() {

      if (!this.$store.state.liveWs) {
        this.$dialog.tips({
          message: "请先到直播账号管理，连接直播间",
          type: "error"
        });
        this.$store.commit("setReplyAiStatus", false)
        return;
      }

      if(!this.status){
        this.$dialog.tips({
          message: "请先打开文字回复",
          type: "error"
        });
        return;
      }


      this.$store.commit("setReplyAiStatus", !this.aiStatus)

      if(this.aiStatus){
        this.$dialog.tips({
          message: "AI自动回复开启成功",
          type: "success"
        })

      }else{
        this.$dialog.tips({
          message: "AI自动回复关闭成功",
          type: "success"
        })
      }



    },
    previousPage() {
      if (this.queryForm.page > 1) {
        this.queryForm.page--
        this.getControlList();

      }
    },
    nextPage() {
      if (this.queryForm.page < this.queryForm.pageCount) {
        this.queryForm.page++
        this.getControlList();
      }
    }
  }
}
</script>

<style scoped>
.text-reply {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.panel {
  background: linear-gradient(135deg, rgba(35, 35, 35, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%);
  backdrop-filter: blur(15px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(60, 60, 60, 0.5);
  padding: 25px;
  position: relative;
  overflow: hidden;
}


.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(60, 60, 60, 0.5);
}

.panel-title {
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(90deg, #c4b5fd 0%, #a5f3fc 50%, #bbf7d0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.control-settings {
  background: linear-gradient(135deg, rgba(45, 45, 45, 0.95) 0%, rgba(35, 35, 35, 0.95) 100%);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(170, 170, 170, 0.2);
  box-shadow: 0 4px 12px rgba(170, 170, 170, 0.1);
}

.setting-row {
  display: flex;
  gap: 20px;
  align-items: center;
}

.setting-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.setting-item label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(170, 170, 170, 0.9);

}

.setting-select {
  padding: 8px 12px;
  border: 1px solid rgba(170, 170, 170, 0.3);
  border-radius: 6px;
  font-size: 14px;
  background: rgba(50, 50, 50, 0.8);
  color: rgba(170, 170, 170, 0.9);
  min-width: 150px;
  transition: all 0.3s ease;
}

.setting-select:focus {
  outline: none;
  border-color: rgba(170, 170, 170, 0.6);
  box-shadow: 0 0 0 3px rgba(170, 170, 170, 0.1);
}

.custom-select {
  position: relative;
  min-width: 150px;
  padding: 8px 12px;
  border: 1px solid rgba(170, 170, 170, 0.3);
  border-radius: 6px;
  font-size: 14px;
  background: rgba(50, 50, 50, 0.8);
  color: rgba(170, 170, 170, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.custom-select:hover {
  border-color: rgba(170, 170, 170, 0.5);
}

.custom-select:focus {
  outline: none;
  border-color: rgba(170, 170, 170, 0.6);
  box-shadow: 0 0 0 3px rgba(170, 170, 170, 0.1);
}

.custom-select-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-arrow {
  font-size: 12px;
  color: rgba(170, 170, 170, 0.6);
  margin-left: 8px;
  transition: transform 0.3s ease;
}

.custom-select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: rgba(35, 35, 35, 0.98);
  border: 1px solid rgba(170, 170, 170, 0.3);
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  z-index: 100;
  overflow: hidden;
}

.dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dropdown-item:hover {
  background: rgba(170, 170, 170, 0.1);
}

.dropdown-item.selected {
  background: rgba(139, 92, 246, 0.2);
  color: #c4b5fd;
}

.group-item {
  justify-content: space-between;
}

.group-item span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-group-btn {
  background: none;
  border: none;
  color: rgba(239, 68, 68, 0.6);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 0;
}

.group-item:hover .delete-group-btn {
  opacity: 1;
}

.delete-group-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.frequency-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.frequency-input {
  width: 60px;
  padding: 8px;
  border: 1px solid rgba(170, 170, 170, 0.3);
  border-radius: 6px;
  text-align: center;
  background: rgba(50, 50, 50, 0.8);
  color: rgba(170, 170, 170, 0.9);
  transition: all 0.3s ease;
}

.frequency-input:focus {
  outline: none;
  border-color: rgba(170, 170, 170, 0.6);
}

.frequency-separator {
  color: rgba(170, 170, 170, 0.7);
}

.frequency-unit {
  color: rgba(170, 170, 170, 0.7);
  font-size: 14px;
}

.checkbox-group {
  display: flex;
  gap: 25px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: rgba(170, 170, 170, 0.9);
  padding: 10px 16px;
  background: rgba(170, 170, 170, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(170, 170, 170, 0.3);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.checkbox-item:hover {
  background: rgba(170, 170, 170, 0.2);
  border-color: rgba(170, 170, 170, 0.5);
  color: #e0e0e0;
}

.checkbox-item input[type="checkbox"] {
  display: none;
}

.checkbox-item .checkbox-custom {
  width: 44px;
  height: 24px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(34, 211, 238, 0.2) 100%);
  border-radius: 12px;
  position: relative;
  border: 1px solid rgba(139, 92, 246, 0.3);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.checkbox-item .checkbox-custom::before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
  border-radius: 50%;
  top: 1px;
  left: 1px;
  box-shadow: 0 3px 8px rgba(139, 92, 246, 0.4);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.checkbox-item input[type="checkbox"]:checked + .checkbox-custom {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.6) 0%, rgba(34, 211, 238, 0.5) 100%);
}

.checkbox-item input[type="checkbox"]:checked + .checkbox-custom::before {
  left: 21px;
  background: linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%);
  box-shadow: 0 3px 8px rgba(255, 255, 255, 0.3);
}

.search-section {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.search-input {
  flex: 1;
  padding: 12px 15px;
  border: 1px solid rgba(80, 80, 80, 0.5);
  border-radius: 8px;
  font-size: 14px;
  background: rgba(40, 40, 40, 0.9);
  color: #ccc;
}

.keywords-list {
  background: linear-gradient(135deg, rgba(45, 45, 45, 0.95) 0%, rgba(35, 35, 35, 0.95) 100%);
  border-radius: 12px;
  border: 1px solid rgba(170, 170, 170, 0.2);
  margin-bottom: 20px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(170, 170, 170, 0.1);
}

.list-header {
  display: grid;
  grid-template-columns: 70px  220px 460px 1fr 70px;
  gap: 10px;
  padding: 15px 20px;
  background: rgba(170, 170, 170, 0.1);
  font-weight: 600;
  font-size: 14px;
  color: rgba(170, 170, 170, 0.9);
  border-bottom: 1px solid rgba(170, 170, 170, 0.2);
}

.keyword-item {
  display: grid;
  grid-template-columns:70px 220px 460px 1fr 50px;
  gap: 10px;
  padding: 15px 20px;
  border-bottom: 1px solid rgba(170, 170, 170, 0.15);
  transition: all 0.3s ease;
  color: rgba(170, 170, 170, 0.9);
}

.keyword-item:hover {
  background: rgba(170, 170, 170, 0.1);
}

.keyword-item:last-child {
  border-bottom: none;
}

.item-checkbox,
.header-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-actions {
  justify-self: end;
}

.item-actions {
  justify-self: end;
}

.item-keyword {
  font-weight: 600;
  color: rgba(170, 170, 170, 0.9);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

}

.item-content {
  color: rgba(170, 170, 170, 0.7);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-status {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-status {
  padding: 4px 12px;
  border-radius: 12px;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-status.enabled {
  background: #666;
  color: #eee;
}

.btn-status.disabled {
  background: #444;
  color: #888;
}

.item-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.btn-action {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 15px;
}

.btn-action.edit {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(37, 99, 235, 0.4) 100%);
  color: #93c5fd;
  border-color: rgba(147, 197, 253, 0.4);
}

.btn-action.edit:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(37, 99, 235, 0.5) 100%);
  color: #bfdbfe;
  border-color: rgba(147, 197, 253, 0.6);
  transform: translateY(-2px);
}

.btn-action.delete {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.btn-action.delete:hover {
  background: rgba(239, 68, 68, 0.4);
  color: #fca5a5;
  transform: translateY(-2px);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  border-top: 1px solid rgba(60, 60, 60, 0.5);
}

.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(139, 92, 246, 0.3);
  background: rgba(139, 92, 246, 0.1);
  color: #a78bfa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #8b5cf6 0%, #22d3ee 100%);
  color: #fff;
  border-color: #8b5cf6;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: rgba(170, 170, 170, 0.8);
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: linear-gradient(135deg, rgba(45, 45, 45, 0.98) 0%, rgba(35, 35, 35, 0.98) 100%);
  border-radius: 16px;
  width: 90%;
  max-width: 50%;
  box-shadow: 0 20px 60px rgba(170, 170, 170, 0.1);
  border: 1px solid rgba(170, 170, 170, 0.3);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid rgba(170, 170, 170, 0.25);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: rgba(170, 170, 170, 0.9);
}

.close-modal {
  background: rgba(170, 170, 170, 0.2);
  border: 1px solid rgba(170, 170, 170, 0.3);
  font-size: 28px;
  color: rgba(170, 170, 170, 0.9);
  cursor: pointer;
  line-height: 1;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-modal:hover {
  background: rgba(170, 170, 170, 0.3);
  color: #e0e0e0;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(170, 170, 170, 0.9);
  font-weight: 600;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(170, 170, 170, 0.4);
  border-radius: 8px;
  font-size: 14px;
  background: rgba(30, 30, 30, 0.9);
  color: rgba(170, 170, 170, 0.9);
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: rgba(170, 170, 170, 0.6);
  box-shadow: 0 0 0 3px rgba(170, 170, 170, 0.2);
}

.form-textarea {
  resize: vertical;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid rgba(170, 170, 170, 0.25);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-field-control {
  background: linear-gradient(135deg, rgba(0, 100, 251, 0.7) 0%, rgba(0, 51, 255, 0.7) 100%);
  color: #ffffff;
  border: 1px solid rgba(0, 81, 255, 0.5);
  box-shadow: 0 4px 12px rgba(0, 82, 255, 0.3);
}

.btn-field-control:hover {
  background: linear-gradient(135deg, rgba(0, 52, 255, 0.7) 0%, rgba(9, 1, 253, 0.7) 100%);
}

.btn-field-control-active {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.7) 0%, rgba(220, 38, 38, 0.7) 100%) !important;
  border-color: rgba(248, 113, 113, 0.5) !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3) !important;
}


.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: rgba(139, 92, 246, 0.2);
  transition: left 0.3s ease;
  z-index: -1;
}

.btn:hover::before {
  left: 0;
}

.btn-primary {
  background: linear-gradient(135deg, rgba(100, 100, 100, 0.7) 0%, rgba(80, 80, 80, 0.7) 100%);
  color: rgba(170, 170, 170, 0.9);
  box-shadow: 0 4px 12px rgba(170, 170, 170, 0.15);
  border: 1px solid rgba(170, 170, 170, 0.3);
}

.btn-import {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.7) 0%, rgba(37, 99, 235, 0.7) 100%);
  color: #ffffff;
  border: 1px solid rgba(147, 197, 253, 0.5);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-import:hover {
  background: linear-gradient(135deg, rgba(147, 197, 253, 0.7) 0%, rgba(59, 130, 246, 0.7) 100%);
}

.btn-export {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.7) 0%, rgba(22, 163, 74, 0.7) 100%);
  color: #ffffff;
  border: 1px solid rgba(74, 222, 128, 0.5);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.btn-export:hover {
  background: linear-gradient(135deg, rgba(74, 222, 128, 0.7) 0%, rgba(34, 197, 94, 0.7) 100%);
}

.btn-secondary {
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.6) 0%, rgba(75, 85, 99, 0.6) 100%);
  color: #d1d5db;
  border: 1px solid rgba(156, 163, 175, 0.5);
}

.btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-sm {
  padding: 8px 12px;
  font-size: 14px;
}

.btn-search {
  background: rgba(80, 80, 80, 0.3);
  color: #aaa;
  border: 1px solid rgba(100, 100, 100, 0.3);
  width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-ai {
  background: linear-gradient(135deg, #22d3ee 0%, #06b6d4 50%, #0891b2 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(34, 211, 238, 0.3);
  border: 1px solid rgba(34, 211, 238, 0.4);
}

.btn-ai:hover {
  background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 50%, #0284c7 100%);
  box-shadow: 0 6px 20px rgba(34, 211, 238, 0.4);
}

.btn-ai.ai-enabled {
  background: linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  border-color: rgba(16, 185, 129, 0.5);
}

.btn-ai.ai-enabled:hover {
  background: linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(100, 100, 100, 0.4);
}

@media (max-width: 768px) {
  .header-actions {
    flex-wrap: wrap;
    gap: 8px;
  }

  .setting-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .list-header,
  .keyword-item {
    grid-template-columns: 40px 1fr;
    gap: 10px;
  }

  .list-header > div:not(:nth-child(1)):not(:nth-child(2)),
  .keyword-item > div:not(:nth-child(1)):not(:nth-child(2)) {
    display: none;
  }

  .pagination {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
}

.insert-content {
  margin-top: 12px;
  border-top: 1px solid rgba(60, 60, 60, 0.3);
}

.insert-label {
  font-size: 13px;
  color: #ccc;
  margin-bottom: 8px;
  font-weight: 600;
}

.insert-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.insert-buttons .btn {
  font-size: 12px;
  padding: 6px 12px;
}

.weight-range {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 12px;
  background: rgba(50, 50, 50, 0.8);
  border-radius: 8px;
  border: 1px solid rgba(70, 70, 70, 0.5);
}

.range-label {
  font-size: 13px;
  color: #aaa;
}

.range-unit {
  font-size: 13px;
  color: #aaa;
}

.range-equal {
  font-size: 16px;
  color: #888;
  font-weight: bold;
}

.range-input {
  width: 70px;
  padding: 8px 10px;
  border: 1px solid rgba(80, 80, 80, 0.5);
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
  background: rgba(40, 40, 40, 0.8);
  color: #ccc;
}

.range-text {
  flex: 1;
  min-width: 120px;
  padding: 8px 10px;
  border: 1px solid rgba(80, 80, 80, 0.5);
  border-radius: 6px;
  font-size: 13px;
  background: rgba(40, 40, 40, 0.8);
  color: #ccc;
}

.variable-tools {

  margin-top: 15px;
  border-radius: 10px;
}

.btn-variable-main {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, rgba(100, 100, 100, 0.5) 0%, rgba(80, 80, 80, 0.5) 100%);
  border: 1px solid rgba(170, 170, 170, 0.3);
  border-radius: 8px;
  color: #c4b5fd;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-variable-main:hover {
  background: linear-gradient(135deg, rgba(120, 120, 120, 0.6) 0%, rgba(100, 100, 100, 0.6) 100%);
  border-color: rgba(170, 170, 170, 0.5);
}

.btn-variable-main i {
  font-size: 16px;
  color: #c4b5fd;
}

.btn-variable-small {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(37, 99, 235, 0.5) 100%);
  border: 1px solid rgba(147, 197, 253, 0.5);
  border-radius: 6px;
  color: #93c5fd;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-variable-small:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.6) 0%, rgba(37, 99, 235, 0.6) 100%);
  border-color: rgba(147, 197, 253, 0.7);
}

.timed-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 15px;
  background: rgba(170, 170, 170, 0.15);
  border-radius: 8px;
  border: 1px solid rgba(170, 170, 170, 0.25);
}

.timed-label {
  font-size: 13px;
  color: rgba(170, 170, 170, 0.9);
  font-weight: 500;
}

.switch-mini {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  vertical-align: middle;
}

.switch-checkbox {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(139, 92, 246, 0.3);
  transition: 0.3s;
  border-radius: 24px;
}

.switch-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(139, 92, 246, 0.3);
}

.switch-checkbox:checked + .switch-slider {
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
}

.switch-checkbox:checked + .switch-slider:before {
  transform: translateX(20px);
  background: linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%);
}

.timed-input-group {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(60, 60, 60, 0.8);
  border-radius: 6px;
  border: 1px solid rgba(80, 80, 80, 0.3);
  transition: all 0.3s ease;
}

.timed-input-group.active {
  background: rgba(80, 80, 80, 0.3);
}

.timed-input {
  width: 45px;
  padding: 4px;
  border: none;
  background: transparent;
  font-size: 13px;
  text-align: center;
  color: #ccc;
}

.timed-input:focus {
  outline: none;
}

.timed-suffix {
  font-size: 12px;
  color: #888;
}
</style>